import { useEffect, useMemo, useRef, useState } from "react";
import {
  CONTENT_MANAGEMENT_SERVICE_URL_AUTH,
  CONTENT_DELIVERY_SERVICE_URL_AUTH,
  LRS_ENDPOINT,
} from "../common/constants";
import { getNodeEnvironment, NodeEnvironment } from "../common/nodeEnvironment";
import { formatActor } from "../common/utilityFunctions";
import "./Renderer.scss";
import { trackIframeError, trackIframeLoad } from "../clientMonitoring/rumClient";
import { iframeErrorMonitor } from "../clientMonitoring/iframeErrorMonitor";

export interface RendererProps {
  userId?: string;
  vibeId?: string;
  activityId?: string;
  catalogModuleId: string;
  catalogProductId: string | null;
  registrationId: string | null;
  context?: string | null;
  isLcmsPreview?: boolean;
  fileEntityId?: string;
  fileEntityVersion?: string;
  isLoading?: boolean;
  setIsLoading?: (args: any) => void;
}

export const Renderer = (props: RendererProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const stableTimestamp = useRef(Date.now().toString()).current;
  const iFrameStartTime = useRef(Date.now()).current;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // if we are serving free content, then we need to go ahead and render the full /index.html path here
  // this is because we do not have CDS to redirect for us, which gives the cousre player the proper context
  // otherwise, the course player thinks the assets are stored in Renderer
  // this was found through testing
  const currentUrlParams = new URLSearchParams(window.location.search);
  const devServer = currentUrlParams.get("dev_server");
  const cdsPreview = currentUrlParams.get("cds_preview");

  const cdsDomain =
    props.isLcmsPreview && cdsPreview !== "true"
      ? CONTENT_MANAGEMENT_SERVICE_URL_AUTH[getNodeEnvironment().toUpperCase() as keyof typeof NodeEnvironment]
      : CONTENT_DELIVERY_SERVICE_URL_AUTH[getNodeEnvironment().toUpperCase() as keyof typeof NodeEnvironment];
  const lrsEndpoint = LRS_ENDPOINT[getNodeEnvironment().toUpperCase() as keyof typeof NodeEnvironment];

  // Memoize the rendering URL to prevent it from changing on re-renders
  const renderingUrl = useMemo(() => {
    // the URL we are rendering here is the CDS url from above
    // we are also appending some query params mainly for LRS and course player
    // these include the LRS endpoint, the actor information (user information including id and vibe id), catalog module id
    const url = new URL(`${cdsDomain}`);
    url.searchParams.append("endpoint", lrsEndpoint);
    url.searchParams.append("actor", formatActor(props.userId, props.vibeId));
    url.searchParams.append("module_id", props.catalogModuleId);

    if (devServer) {
      url.searchParams.append("dev_server", devServer);
    }

    // registration id is only applicable in the 1P use case, so it is optional
    // if the use case is LCMS preview, we do not want to set the registration id in any case, regardless of whether it is present or not.
    if (props.registrationId && !props.isLcmsPreview) {
      // Note: We are passing both these values because registration_id is passed in query param to LRS
      // while registration is passed in body according to the xapi course player spec
      url.searchParams.append("registration_id", props.registrationId);
      url.searchParams.append("registration", props.registrationId);
    }

    if (props.catalogProductId) {
      url.searchParams.append("product_id", props.catalogProductId);
    }

    if (props.activityId) {
      url.searchParams.append("activity_id", props.activityId);
    }

    if (props.context && props.context === "aci") {
      url.searchParams.append("context", "aci");
    }

    // Cache Busting query param. Specifically firefox was aggresively caching requests in the context of ELTI
    // adding a cache busting parameter here along with tweaks to error code handling in the CDS in order to invalidate
    // the browser cache
    // Using a stable value for the cache busting parameter to prevent unnecessary reloads
    url.searchParams.append("_cb", stableTimestamp);

    /**
     * Append file entity parameters for LCMS preview mode
     * When in LCMS preview mode and both fileEntityId and fileEntityVersion are available,
     * add these as query parameters to enable preview of specific file entity versions
     * @param fileEntityId - Unique identifier for the file entity
     * @param fileEntityVersion - Version number of the file entity to preview
     */
    if (props.isLcmsPreview && props.fileEntityId && props.fileEntityVersion) {
      url.searchParams.append("file_entity_id", props.fileEntityId);
      url.searchParams.append("file_entity_version", props.fileEntityVersion);
    }

    if (cdsPreview === "true") {
      url.searchParams.append("cds_preview", cdsPreview);
    }

    return url.toString();
  }, [
    cdsDomain,
    lrsEndpoint,
    props.userId,
    props.vibeId,
    props.catalogModuleId,
    devServer,
    cdsPreview,
    props.registrationId,
    props.isLcmsPreview,
    props.catalogProductId,
    props.activityId,
    props.context,
    props.fileEntityId,
    props.fileEntityVersion,
    stableTimestamp,
  ]);

  // Initialize RUM monitoring
  useEffect(() => {
    // Safe RUM initialization that works in both browser and test environments
    const initializeRum = async () => {
      try {
        if (typeof window !== "undefined") {
          const rumClientModule = await import("../clientMonitoring/rumClient");
          if (typeof rumClientModule.rumInit === "function") {
            rumClientModule.rumInit(false); // Cookies disabled to prevent event deduplication
          }
        }
      } catch (error) {
        console.warn("RUM initialization skipped:", error);
      }
    };

    initializeRum();
  }, []);

  useEffect(() => {
    if (iframeRef.current && !isLoading) {
      // Test iframe access to verify same-origin configuration
      const hasAccess = iframeErrorMonitor.testIframeAccess(iframeRef.current);

      if (hasAccess) {
        // Start enhanced error monitoring
        iframeErrorMonitor.startMonitoring(iframeRef.current);
        console.log("Enhanced iframe level monitoring started!");
      } else {
        console.warn("Enhanced iframe level monitoring unavailable!");
      }
    }

    // Cleanup monitoring on unmount
    return () => {
      iframeErrorMonitor.stopMonitoring();
    };
  }, [isLoading]);

  // Effect to reload iframe after parent container and CSS styles are loaded
  useEffect(() => {
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = "";
      if (iframeRef.current) {
        iframeRef.current.src = currentSrc;
      }
    }
  }, [isLoading]);

  return (
    <div className="rendererIframeDiv">
      <iframe
        hidden={isLoading}
        ref={iframeRef}
        title="Training Content"
        src={renderingUrl}
        width="100%"
        height="100%"
        aria-label="Training Content"
        data-testid="renderer_iframe"
        id="renderer_iframe"
        onLoad={() => {
          setIsLoading(false);
          props.setIsLoading ? props.setIsLoading(false) : {};
          trackIframeLoad(iFrameStartTime);
        }}
        onError={() => {
          trackIframeError();
        }}
        loading={"eager"}
      ></iframe>
    </div>
  );
};
